import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Scales, EnvelopeSimple, ArrowLeft, CheckCircle, Copy } from '@phosphor-icons/react';

import { API } from '@/lib/api';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      if (data.reset_token) setResetToken(data.reset_token);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetLink = resetToken ? `${window.location.origin}/reset-password?token=${resetToken}` : '';

  const copyLink = () => {
    navigator.clipboard.writeText(resetLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <Scales weight="duotone" className="w-8 h-8 text-[#002FA7]" />
          <span className="font-outfit text-2xl font-bold text-[#09090B]">ClarityCase AI</span>
        </div>

        {sent ? (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle size={32} className="text-[#34C759]" />
              <h1 className="font-outfit text-2xl font-bold text-[#09090B]">Reset Link Generated</h1>
            </div>
            <p className="text-[#6B7280] font-ibm mb-6">
              A password reset link has been generated. Use the link below to reset your password.
            </p>
            {resetLink && (
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 mb-6" data-testid="reset-link-container">
                <label className="block text-xs font-bold tracking-[0.2em] uppercase text-[#6B7280] mb-2 font-ibm">Reset Link</label>
                <div className="flex gap-2">
                  <input readOnly value={resetLink} data-testid="reset-link-input"
                    className="flex-1 px-3 py-2 border border-[#E5E7EB] rounded-md text-sm font-mono bg-[#F9FAFB] text-[#09090B]" />
                  <button onClick={copyLink} data-testid="copy-reset-link"
                    className="px-3 py-2 bg-[#002FA7] text-white rounded-md hover:bg-[#00237D] transition-all flex items-center gap-1.5 text-sm font-ibm">
                    <Copy size={16} /> {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="text-xs text-[#6B7280] font-ibm mt-2">This link expires in 1 hour.</p>
              </div>
            )}
            <Link to={resetLink ? `/reset-password?token=${resetToken}` : '/login'} data-testid="go-to-reset"
              className="block w-full text-center py-3 bg-[#002FA7] text-white font-ibm font-semibold rounded-md hover:bg-[#00237D] transition-all">
              {resetToken ? 'Reset Password Now' : 'Back to Sign In'}
            </Link>
          </div>
        ) : (
          <div>
            <h1 className="font-outfit text-3xl font-bold text-[#09090B] mb-1">Forgot Password</h1>
            <p className="text-[#6B7280] font-ibm mb-8">Enter your email address and we'll generate a password reset link.</p>

            {error && <div data-testid="forgot-error" className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm font-ibm">{error}</div>}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-xs font-bold tracking-[0.2em] uppercase text-[#6B7280] mb-2 font-ibm">Email Address</label>
                <div className="relative">
                  <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
                  <input data-testid="forgot-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} required
                    className="w-full pl-10 pr-4 py-3 border border-[#E5E7EB] rounded-md bg-white text-[#09090B] font-ibm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent transition-all"
                    placeholder="you@example.com" />
                </div>
              </div>
              <button data-testid="forgot-submit-button" type="submit" disabled={loading}
                className="w-full py-3 bg-[#002FA7] text-white font-ibm font-semibold rounded-md hover:bg-[#00237D] active:scale-[0.98] transition-all disabled:opacity-50">
                {loading ? 'Generating Link...' : 'Generate Reset Link'}
              </button>
            </form>
          </div>
        )}

        <p className="mt-6 text-center text-sm text-[#6B7280] font-ibm">
          <Link to="/login" className="flex items-center gap-1 justify-center text-[#002FA7] font-semibold hover:underline" data-testid="back-to-login">
            <ArrowLeft size={14} /> Back to Sign In
          </Link>
        </p>
      </div>
    </div>
  );
}
